import React, { useState, useContext, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import {
  FaBars, FaTimes, FaBox, FaClipboardList, FaUsers, FaShoppingCart,
  FaFileAlt, FaCog, FaShieldAlt, FaSignOutAlt, FaChevronDown,
  FaChartLine, FaExchangeAlt, FaDollarSign, FaLayerGroup, FaHome, FaBirthdayCake, FaRocket, FaUserCircle, FaThLarge, FaQuestionCircle, FaTags, FaAngleDoubleLeft, FaAngleDoubleRight
} from "react-icons/fa";
import api from "../api";
import { AuthContext } from "../context/AuthContext";
import { moduloLigado, MODULO_ETIQUETAS } from "../config/modulos";
import BotaoLeitor from "./BotaoLeitor";
const logo = "/metria-logo.svg";

const Navbar = () => {
  const { isAuthenticated, isSuperAdmin, tenant, logout } = useContext(AuthContext);
  const temEtiquetas = moduloLigado(tenant, MODULO_ETIQUETAS);
  const [mobileOpen, setMobileOpen] = useState(false);
  // Recolher a sidebar e decisao que a pessoa toma uma vez e espera encontrar
  // do mesmo jeito amanha — por isso localStorage, e nao estado de sessao.
  // Vale so no layout largo; abaixo de 768px a sidebar ja e uma gaveta.
  const [recolhida, setRecolhida] = useState(() => localStorage.getItem('sidebar_recolhida') === 'true');
  useEffect(() => {
    document.body.classList.toggle('sidebar-recolhida', recolhida);
    localStorage.setItem('sidebar_recolhida', recolhida ? 'true' : 'false');
    // Sem cleanup que remova a classe: o Navbar so desmonta no logout, e ai a
    // tela de login nao tem sidebar para a classe atrapalhar.
  }, [recolhida]);
  const [expanded, setExpanded] = useState({});
  // 'true' apenas se cache confirmar (evita esconder Primeiros Passos para usuarios novos).
  const [onboardingCompleto, setOnboardingCompleto] = useState(() => {
    return localStorage.getItem('onboarding_completo') === 'true';
  });
  const location = useLocation();

  useEffect(() => { setMobileOpen(false); }, [location.pathname]);

  useEffect(() => {
    if (!isAuthenticated) return;
    // Uma vez marcado como completo, nao re-verifica (otimizacao + evita flicker).
    if (localStorage.getItem('onboarding_completo') === 'true') return;
    api.get('/onboarding/status').then(res => {
      setOnboardingCompleto(res.data.completo);
      localStorage.setItem('onboarding_completo', String(res.data.completo));
    }).catch(() => {});
  }, [isAuthenticated, location.pathname]);

  const toggle = (key) => setExpanded(p => ({ ...p, [key]: !p[key] }));
  // Compara so o caminho: itens que apontam para uma aba (`?aba=estoque`)
  // marcavam como inativos, porque a querystring nunca casa com o pathname.
  const isActive = (destino) => {
    const path = String(destino).split('?')[0];
    if (path === '/') return location.pathname === '/';
    return location.pathname === path || location.pathname.startsWith(path + '/');
  };

  if (!isAuthenticated) return null;

  const tenantLogo = logo;

  const MenuItem = ({ to, icon, label, indent }) => (
    <Link to={to} className={`sidebar-item ${indent ? 'sidebar-sub' : ''} ${isActive(to) ? 'active' : ''}`}>
      {icon && <span className="sidebar-item-icon">{icon}</span>}
      <span>{label}</span>
    </Link>
  );

  const SubMenu = ({ id, icon, label, children }) => (
    <>
      <div className="sidebar-item" onClick={() => toggle(id)} style={{ cursor: 'pointer' }}>
        <span className="sidebar-item-icon">{icon}</span>
        <span style={{ flex: 1 }}>{label}</span>
        <FaChevronDown style={{ fontSize: 10, transition: 'transform 0.2s', transform: expanded[id] ? 'rotate(180deg)' : 'none', color: '#BBA9A9' }} />
      </div>
      {expanded[id] && <div className="sidebar-sub">{children}</div>}
    </>
  );

  const sidebarContent = (
    <>
      {/* Header */}
      <div className="sidebar-header">
        <img src={tenantLogo} alt="Logo" className="sidebar-logo" />
        <span className="sidebar-brand">{tenant?.nome || "Gestão"}</span>
        <button className="sidebar-recolher" onClick={() => setRecolhida(true)}
          title="Recolher menu" aria-label="Recolher menu">
          <FaAngleDoubleLeft />
        </button>
      </div>

      {/* Onboarding */}
      {!onboardingCompleto && (
        <div className="sidebar-section">
          <MenuItem to="/primeiros-passos" icon={<FaRocket />} label="Primeiros Passos" />
        </div>
      )}

      {/* Dashboard */}
      <div className="sidebar-section">
        <MenuItem to="/" icon={<FaHome />} label="Painel" />
        <MenuItem to="/atalhos" icon={<FaThLarge />} label="Atalhos" />
      </div>

      {/* Vendas */}
      <div className="sidebar-section">
        <div className="sidebar-section-label">Vendas</div>
        <MenuItem to="/clientes" icon={<FaUsers />} label="Clientes" />
        <MenuItem to="/pedidos" icon={<FaShoppingCart />} label="Pedidos" />
        <MenuItem to="/orcamentos" icon={<FaFileAlt />} label="Orçamentos" />
        <MenuItem to="/financas" icon={<FaDollarSign />} label="Finanças" />
      </div>

      {/* Produção */}
      <div className="sidebar-section">
        <div className="sidebar-section-label">Produção</div>
        <SubMenu id="fichas" icon={<FaClipboardList />} label="Fichas Técnicas">
          <MenuItem to="/fichas-tecnicas/base" label="Preparação Base" />
          <MenuItem to="/fichas-tecnicas/produto" label="Produto Final" />
        </SubMenu>
        <SubMenu id="bolos" icon={<FaBirthdayCake />} label="Bolos">
          <MenuItem to="/guia/tamanhos" label="Cadastro" />
          <MenuItem to="/guia/recheios" label="Recheios" />
          <MenuItem to="/guia/combinacoes" label="Combinações" />
        </SubMenu>
        <SubMenu id="guia" icon={<FaLayerGroup />} label="Guia">
          <MenuItem to="/guia/montagem" label="Montagem Diária" />
          <MenuItem to="/guia/producao" label="Produção de Bolos" />
          <MenuItem to="/guia/calculadora" label="Calculadora" />
        </SubMenu>
      </div>

      {/* Estoque */}
      <div className="sidebar-section">
        <div className="sidebar-section-label">Estoque</div>
        <SubMenu id="produtos" icon={<FaBox />} label="Produtos">
          <MenuItem to="/estoque" label="Ingredientes" />
          <MenuItem to="/embalagens" label="Embalagens" />
        </SubMenu>
        {/* Logo abaixo de Produtos: a pergunta "o que vence?" e sobre o que esta
            guardado, e e em Estoque que se procura o que esta guardado. */}
        {temEtiquetas && <MenuItem to="/etiquetas" icon={<FaTags />} label="Validade" />}
        <SubMenu id="relatorios" icon={<FaChartLine />} label="Relatórios">
          <MenuItem to="/inventario" label="Inventário" />
          {temEtiquetas
            ? <MenuItem to="/etiquetas?aba=estoque" label="Controle de Lotes" />
            : <MenuItem to="/lotes" label="Controle de Lotes" />}
          <MenuItem to="/movimentacoes" label="Movimentações" />
          <MenuItem to="/variacao-custo" label="Variação de Custo" />
          <MenuItem to="/valor-estoque" label="Valor Estoque" />
          <MenuItem to="/prediction" label="Previsão Compras" />
        </SubMenu>
      </div>

      {/* Footer */}
      <div className="sidebar-footer">
        <MenuItem to="/configuracoes" icon={<FaUserCircle />} label="Minha conta" />
        <MenuItem to="/ajuda" icon={<FaQuestionCircle />} label="Tutoriais" />
        {isSuperAdmin && <MenuItem to="/super-admin" icon={<FaShieldAlt />} label="Super Admin" />}
        <div className="sidebar-item" onClick={logout} style={{ cursor: 'pointer' }}>
          <span className="sidebar-item-icon"><FaSignOutAlt /></span>
          <span>Sair</span>
        </div>
      </div>
    </>
  );

  return (
    <>
      {/* Mobile top bar */}
      <div className="mobile-topbar">
        <button className="mobile-topbar-toggle" onClick={() => setMobileOpen(!mobileOpen)}>
          {mobileOpen ? <FaTimes /> : <FaBars />}
        </button>
        <img src="/metria-icon.svg" alt="Logo" className="mobile-topbar-logo" />
        <span className="mobile-topbar-brand">{tenant?.nome || "Gestão"}</span>
        {/* Encostado na direita pelo `margin-left:auto` do CSS: e o polegar que
            alcanca sem reposicionar a mao. So aparece no app. */}
        <BotaoLeitor />
      </div>

      {/* Volta da sidebar recolhida. Fica fora da <nav> de proposito: a nav
          esta deslocada para fora da tela quando recolhida. */}
      <button className="abrir-sidebar" onClick={() => setRecolhida(false)}
        title="Mostrar menu" aria-label="Mostrar menu">
        <FaAngleDoubleRight />
      </button>

      {/* Overlay */}
      <div className={`sidebar-overlay ${mobileOpen ? 'open' : ''}`} onClick={() => setMobileOpen(false)} />

      {/* Sidebar */}
      <nav className={`sidebar ${mobileOpen ? 'open' : ''}`}>
        {sidebarContent}
      </nav>
    </>
  );
};

export default Navbar;
